import { Content, JSONContent } from '@tiptap/react';

import { getAllForNode } from '@/levity/flows/node-inputs/db';

import { KeyValueConfiguratorValueType } from '../../key-value-configurator';
import {
  ConstructNodeTestResponseParams,
  HTTPSApiRequestTypeOptions,
  TestRequestResponse,
  TestRequestStatus,
} from './http-api-menu.types';

// Constants
export const URL_ID = 'url';
export const ADVANCED_PAYLOAD_ID = 'advanced-payload';

export const HEADER_REFERENCE_CATEGORY = 'header';
export const HEADER_KEY = 'header-key';
export const HEADER_VALUE = 'header-value';

export const PAYLOAD_REFERENCE_CATEGORY = 'payload';
export const PAYLOAD_KEY = 'payload-key';
export const PAYLOAD_VALUE = 'payload-value';

export const REQUEST_TYPE_OPTIONS: HTTPSApiRequestTypeOptions[] = [
  { label: 'POST', id: 'post' },
  { label: 'GET', id: 'get' },
  { label: 'PUT', id: 'put' },
  { label: 'DELETE', id: 'delete' },
  { label: 'PATCH', id: 'patch' },
];

export const LOADING_RESPONSE: TestRequestResponse = {
  status: TestRequestStatus.Loading,
};

// Note: Generates the inital values for the KeyValueConfigurator from node inputs.
export const generateKeyValueConfiguratorValuesFromInputs = ({
  nodeInputs,
  column1Key,
  column2Key,
}: {
  nodeInputs: Awaited<ReturnType<typeof getAllForNode>>[number][];
  column1Key: string;
  column2Key: string;
}) => {
  const kvConfiguratorValues: KeyValueConfiguratorValueType<Content>[] = [];

  // Used to keep track of added rowIds to avoid duplicates
  const addedRowIds = new Set();

  nodeInputs.forEach((nodeInput) => {
    // If the rowId has already been added, skip it
    // Note: We use the referenceKey as the rowId
    // Note: Reference keys are automatically generated by the useKeyValueConfigurator when adding a new row
    if (addedRowIds.has(nodeInput.referenceKey)) return;

    // Find the column values for the current rowId
    const col1Value = nodeInputs.find(
      (input) => input.referenceKey === nodeInput.referenceKey && input.connectedNodeInputId === column1Key,
    )?.rawValue;
    const col2Value = nodeInputs.find(
      (input) => input.referenceKey === nodeInput.referenceKey && input.connectedNodeInputId === column2Key,
    )?.rawValue;

    // Add the rowId and column values to the headerValues array
    if (nodeInput.referenceKey && (col1Value || col2Value)) {
      kvConfiguratorValues.push({
        rowId: nodeInput.referenceKey,
        columns: [
          {
            columnId: column1Key,
            value: col1Value as JSONContent,
          },
          {
            columnId: column2Key,
            value: col2Value as JSONContent,
          },
        ],
      });
      addedRowIds.add(nodeInput.referenceKey);
    }
  });

  return kvConfiguratorValues;
};

export const getUrlRawValueFromNodeInputs = (dynamicInputs: Awaited<ReturnType<typeof getAllForNode>>[number][]) => {
  const url = dynamicInputs.find((input) => input.connectedNodeInputId === URL_ID)?.rawValue;
  return url ? (url as Content) : undefined;
};

export const getAdvancePayloadRawValueFromNodeInputs = (
  dynamicInputs: Awaited<ReturnType<typeof getAllForNode>>[number][],
) => {
  const advancedPayload = dynamicInputs.find((input) => input.connectedNodeInputId === ADVANCED_PAYLOAD_ID)?.rawValue;
  return advancedPayload ? (advancedPayload as Content) : undefined;
};

export const getUrlStringFromNodeInputs = (dynamicInputs: Awaited<ReturnType<typeof getAllForNode>>[number][]) => {
  const url = dynamicInputs.find((input) => input.connectedNodeInputId === URL_ID)?.parsedValue;
  return url ?? undefined;
};

export const constructNodeTestResponse = ({
  isLoading,
  error,
  stopNodeResult,
  lastNodeResults,
}: ConstructNodeTestResponseParams) => {
  if (isLoading) {
    return LOADING_RESPONSE;
  }
  if (!stopNodeResult || !('result' in stopNodeResult)) {
    return {
      status: TestRequestStatus.Error,
      code: 500,
      message: 'The flow did not generate any result',
      data: JSON.stringify(stopNodeResult),
    };
  }
  if (stopNodeResult.result.status === 'success') {
    return {
      status: TestRequestStatus.Success,
      code: 200,
      message: stopNodeResult.result.message,
      data: JSON.stringify(stopNodeResult.result.data),
    };
  }
  if (stopNodeResult.result.status === 'warning') {
    return {
      status: TestRequestStatus.Error,
      code: Number(stopNodeResult.result.errorCode) || 500,
      message: stopNodeResult.result.message,
      data: JSON.stringify(stopNodeResult.result.data),
    };
  }
  if (stopNodeResult === undefined && lastNodeResults && lastNodeResults.length > 0) {
    const lastResult = lastNodeResults[0] && 'result' in lastNodeResults[0] && lastNodeResults[0].result.data;
    return {
      status: TestRequestStatus.Error,
      code: 500,
      message: 'The flow did not reach the node your testing',
      data: JSON.stringify(lastResult ?? {}),
    };
  }
  if (error?.message || error?.data) {
    return {
      status: TestRequestStatus.Error,
      code: 500,
      message: error.message,
      data: JSON.stringify(error.data),
    };
  }
  return {
    status: TestRequestStatus.Error,
    code: 500,
    message: 'An unknown error occurred',
    data: JSON.stringify({}),
  };
};

export const testDialogCopy = (code?: number, message?: string) => {
  return {
    title: 'Test Request',
    states: {
      loading: {
        title: 'Sending request...',
      },
      success: {
        title: 'Test successful',
        subtitle: `${code} - ${message ?? 'HTTP Response'}`,
      },
      error: {
        title: 'Test failed',
        subtitle: message ?? 'Unknown error',
      },
    },
  };
};
